<template>
  <section class="workflow-section section" id="workflow">
    <div class="container">
      <div class="section-header">
        <div class="section-tag">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M3 7h18M3 12h14M3 17h10"/></svg>
          超级工作流矩阵
        </div>
        <h2 class="section-title">点石AI <span class="gradient-text">超级工作流矩阵</span></h2>
        <p class="section-subtitle">
          探索工作流商店，告别繁琐设置，直接套用现成工作流。<br>开箱即用，秒变AI高手
        </p>
      </div>

      <!-- Tabs -->
      <div class="tabs-wrapper">
        <div v-if="tabsLoading" class="tabs-loading">分类加载中…</div>
        <div v-else class="tabs-scroll">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="tab-btn"
            :class="{ active: activeTabMark === tab.markName }"
            @click="switchTab(tab)"
          >{{ tab.markName }}</button>
        </div>
      </div>

      <!-- Workflow Grid -->
      <transition name="fade-tab" mode="out-in">
        <div v-if="listLoading && !currentWorkflows.length" class="workflow-grid workflow-grid-loading" :key="'loading-' + activeTabMark">
          <div class="wf-loading-hint">加载中…</div>
        </div>
        <div v-else-if="!listLoading && !currentWorkflows.length" class="workflow-grid workflow-grid-empty" :key="'empty-' + activeTabMark">
          <div class="wf-empty-hint">该分类下暂无工作流</div>
        </div>
        <div v-else class="workflow-grid" :key="activeTabMark">
          <div
            v-for="(wf, i) in currentWorkflows"
            :key="wf.id"
            class="wf-card"
            :style="{ animationDelay: (i * 0.06) + 's' }"
            role="button"
            tabindex="0"
            @click="openWorkflow(wf)"
            @keydown.enter="openWorkflow(wf)"
          >
            <!-- Card image -->
            <div class="wf-img-wrap">
              <img :src="wf.image" :alt="wf.title" class="wf-img" loading="lazy" />
              <div class="wf-img-overlay">
                <span class="wf-use-btn">立即体验</span>
              </div>
              <div class="wf-category-tag">{{ wf.category }}</div>
            </div>

            <!-- Card body -->
            <div class="wf-body">
              <h4 class="wf-title">{{ wf.title }}</h4>
              <p class="wf-desc">{{ wf.desc }}</p>

              <!-- Benchmark accounts -->
              <div class="wf-accounts" v-if="wf.accounts && wf.accounts.length">
                <div class="accounts-label">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                  对标账号
                </div>
                <div class="accounts-list">
                  <div v-for="(acc, ai) in wf.accounts" :key="acc.name + '-' + ai" class="account-item">
                    <div class="acc-avatar">{{ (acc.name && acc.name[0]) || '—' }}</div>
                    <div class="acc-info">
                      <span class="acc-name">{{ acc.name }}</span>
                      <span class="acc-fans">{{ acc.fans }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="wf-footer">
                <span
                  v-if="cardTagLabel(wf)"
                  class="wf-tag"
                  :class="cardTagClass(wf)"
                >{{ cardTagLabel(wf) }}</span>
                <button type="button" class="wf-action" @click.stop="openWorkflow(wf)">查看详情</button>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- View All -->
      <div class="view-all">
        <router-link :to="WORKFLOW_STORE" class="view-all-btn">
          查看全部 {{ currentCategory }} 工作流
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </router-link>
      </div>

      <!-- Section CTA -->
      <div class="section-cta">
        <router-link :to="WORKFLOW_STORE" class="btn-primary">立即体验</router-link>
        <a href="#" class="btn-outline">预约课程</a>
      </div>
    </div>
  </section>
</template>

<script>
import { getWorkTypeList, getWorkflowList } from '@/api/agents'
import { WORKFLOW_STORE } from '@/constants/homeNav'
import {
  SVIP_FREE_TAB_MARK,
  createSvipFreeTab,
  isBizSuccess,
  extractWorkTypeRows,
  extractWorkflowRows,
  normalizeTabs,
  rowsToNormalizedWorkflows,
  sortAndLimitHomeWorkflows,
} from '@/utils/workflowNormalize'

const HOME_LIST_PAGE_SIZE = 50
const HOME_PREVIEW_LIMIT = 8

export default {
  name: 'WorkflowSection',
  data() {
    return {
      WORKFLOW_STORE,
      tabs: [],
      activeTabMark: '',
      /** markName -> 规范化后的工作流列表（已截断 8 条） */
      listByMark: {},
      tabsLoading: false,
      listLoading: false,
    }
  },
  computed: {
    currentWorkflows() {
      return this.listByMark[this.activeTabMark] || []
    },
    currentCategory() {
      return this.activeTabMark || '工作流'
    },
  },
  mounted() {
    this.fetchTabsAndFirstList()
  },
  methods: {
    async fetchTabsAndFirstList() {
      this.tabsLoading = true
      const svipTab = createSvipFreeTab()
      try {
        const res = await getWorkTypeList()
        if (isBizSuccess(res)) {
          const raw = extractWorkTypeRows(res)
          const normalized = normalizeTabs(raw)
          const rest = normalized.filter((t) => t.markName !== SVIP_FREE_TAB_MARK)
          this.tabs = rest.length ? [svipTab, ...rest] : [svipTab]
        } else {
          this.tabs = [svipTab]
        }
      } catch (e) {
        console.error('首页工作流分类加载失败:', e)
        this.tabs = [svipTab]
      } finally {
        this.tabsLoading = false
      }
      const first = this.tabs[0]
      if (first) {
        this.activeTabMark = first.markName
        await this.fetchListForMark(first.markName)
      }
    },
    async switchTab(tab) {
      const mark = tab.markName
      if (this.activeTabMark === mark) return
      this.activeTabMark = mark
      if (this.listByMark[mark] !== undefined) {
        this.listLoading = false
        return
      }
      await this.fetchListForMark(mark)
    },
    async fetchListForMark(markName) {
      this.listLoading = true
      try {
        const params = { pageNum: 1, pageIndex: 1, pageSize: HOME_LIST_PAGE_SIZE }
        if (markName !== SVIP_FREE_TAB_MARK) {
          params.flowMark = markName
        }
        const res = await getWorkflowList(params)
        if (!isBizSuccess(res)) {
          this.$set(this.listByMark, markName, [])
          return
        }
        const rows = extractWorkflowRows(res)
        const normalized = rowsToNormalizedWorkflows(rows)
        const sorted = sortAndLimitHomeWorkflows(normalized, HOME_PREVIEW_LIMIT)
        this.$set(this.listByMark, markName, sorted)
      } catch (e) {
        console.error('首页工作流列表加载失败:', e)
        this.$set(this.listByMark, markName, [])
      } finally {
        this.listLoading = false
      }
    },
    openWorkflow(wf) {
      if (!wf || wf.id == null || String(wf.id) === '') return
      this.$router.push({ name: 'WorkflowDetail', params: { id: String(wf.id) } })
    },
    cardTagLabel(wf) {
      if (!wf) return null
      if (wf.isHotFlag) return 'HOT'
      if (wf.isSvipFree) return 'FREE'
      const tags = wf.tags || []
      const hotish = tags.find((t) => t && /热门|火爆|hot/i.test(String(t)))
      if (hotish) return String(hotish)
      const nw = tags.find((t) => t && /新品|new/i.test(String(t)))
      if (nw) return String(nw)
      return tags[0] ? String(tags[0]) : null
    },
    cardTagClass(wf) {
      const label = this.cardTagLabel(wf)
      if (!label) return 'muted'
      const s = label.toLowerCase()
      if (s === 'hot' || /热|火爆/.test(label)) return 'hot'
      if (s === 'free' || /免|svip/.test(label)) return 'free'
      if (s === 'new' || /新品/.test(label)) return 'new'
      return 'muted'
    },
  },
}
</script>

<style scoped>
.workflow-section { background: var(--bg-section); }

.section-header { text-align: center; margin-bottom: 48px; }
.section-header .section-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  background: var(--primary-light);
  color: var(--primary);
  border-radius: 100px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
}

/* Tabs */
.tabs-wrapper {
  position: relative;
  margin-bottom: 36px;
}
.tabs-scroll {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tab-btn {
  padding: 8px 18px;
  border-radius: 100px;
  border: 1.5px solid var(--border);
  background: var(--bg-card);
  color: var(--text-muted);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;
}
.tab-btn:hover {
  border-color: var(--primary);
  color: var(--primary);
  background: var(--primary-light);
}
.tab-btn.active {
  background: var(--gradient-brand);
  border-color: transparent;
  color: white;
  box-shadow: var(--shadow);
}

.tabs-loading {
  font-size: 0.85rem;
  color: var(--text-muted);
  padding: 8px 0 4px;
}

/* Grid */
.workflow-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 36px;
}

.workflow-grid-loading,
.workflow-grid-empty {
  min-height: 200px;
  place-items: center;
  display: grid;
}
.wf-loading-hint,
.wf-empty-hint {
  font-size: 0.9rem;
  color: var(--text-muted);
}

.wf-card {
  background: var(--bg-card);
  border: 1.5px solid var(--border);
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.28s ease;
  display: flex;
  flex-direction: column;
  animation: fadeInUp 0.4s ease-out both;
}
.wf-card:hover {
  transform: translateY(-5px);
  border-color: var(--primary);
  box-shadow: var(--shadow-lg);
}

.wf-img-wrap {
  position: relative;
  height: 160px;
  overflow: hidden;
  background: var(--bg-alt);
  flex-shrink: 0;
}
.wf-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}
.wf-card:hover .wf-img { transform: scale(1.05); }

.wf-img-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%);
  opacity: 0;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 16px;
}
.wf-card:hover .wf-img-overlay { opacity: 1; }
.wf-use-btn {
  padding: 8px 20px;
  background: white;
  color: var(--primary);
  border-radius: 100px;
  font-size: 0.82rem;
  font-weight: 700;
}

.wf-category-tag {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 3px 10px;
  background: rgba(0,0,0,0.5);
  color: white;
  border-radius: 100px;
  font-size: 0.7rem;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.wf-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.wf-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
  line-height: 1.4;
}

.wf-desc {
  font-size: 0.76rem;
  color: var(--text-muted);
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.wf-accounts {
  border-top: 1px solid var(--border-light);
  padding-top: 8px;
  margin-top: 4px;
}
.accounts-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.68rem;
  color: var(--text-muted);
  font-weight: 600;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.accounts-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 80px;
  overflow: hidden;
}
.account-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.acc-avatar {
  width: 20px; height: 20px;
  border-radius: 50%;
  background: var(--gradient-brand);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.acc-info { display: flex; align-items: center; gap: 6px; }
.acc-name { font-size: 0.72rem; color: var(--text-secondary); font-weight: 500; }
.acc-fans { font-size: 0.68rem; color: var(--text-muted); }

.wf-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 8px;
}
.wf-tag {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
}
.wf-tag.hot { background: rgba(239,68,68,0.1); color: #EF4444; }
.wf-tag.new { background: rgba(16,185,129,0.1); color: #10B981; }
.wf-tag.free { background: rgba(37,99,235,0.1); color: var(--primary); }
.wf-tag.muted { background: var(--bg-alt); color: var(--text-muted); }

.wf-action {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 5px 12px;
  border: 1.5px solid var(--primary);
  color: var(--primary);
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.wf-card:hover .wf-action { background: var(--primary); color: white; }

/* View All */
.view-all {
  text-align: center;
  margin-bottom: 40px;
}
.view-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  border: 1.5px solid var(--border);
  border-radius: 100px;
  color: var(--text-secondary);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}
.view-all-btn:hover {
  border-color: var(--primary);
  color: var(--primary);
  background: var(--primary-light);
}

.section-cta {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

/* Tab transition */
.fade-tab-enter-active, .fade-tab-leave-active { transition: all 0.25s ease; }
.fade-tab-enter, .fade-tab-leave-to { opacity: 0; transform: translateY(12px); }

@media (max-width: 1100px) { .workflow-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 800px) { .workflow-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 480px) { .workflow-grid { grid-template-columns: 1fr; } }
</style>
